import type { ITotalData } from '@/api/types/cart';
import { IS_US } from '@/utils/constants';
import type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferEnableInput,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

export type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferCouponMode,
  AutoOfferEnableInput,
  AutoOfferPointMode,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

export const PAYMENT_AUTO_OFFER_TOAST_STORAGE_KEY = 'zl-payment-auto-offer-toast';

export const normalizeAmount = (amount?: string | number | null) => {
  if (amount === null || amount === undefined || amount === '') {
    return 0;
  }
  const value = Number(String(amount).replace(/,/g, ''));
  return Number.isFinite(value) ? value : 0;
};

export const shouldEnableManualOffer = ({ selectedCount = 0 }: AutoOfferEnableInput) =>
  IS_US && selectedCount > 0;

/** 自动资格只来自新实验，游客不能自动选券。 */
export const shouldAutoApplyOffer = (state?: AutoOfferStore) =>
  Boolean(state?.enabled && state.authenticated && state.couponPolicy === 'automatic');

/** 只有用户显式恢复或后端明确判定券失效才能调用；积分保持独立。 */
export const clearAutoOfferCouponSelection = (state: AutoOfferStore): AutoOfferStore => ({
  ...state,
  couponMode: 'auto',
  coupon: undefined,
  code: '',
  couponText: '',
});

export const clearFailedCouponSelection = (
  state: AutoOfferStore,
  explicitlyInvalid = false,
): AutoOfferStore =>
  explicitlyInvalid && ['manual_coupon', 'manual_code'].includes(state.couponMode)
    ? clearAutoOfferCouponSelection(state)
    : state;

/** 网络错误不证明用户的券失效；只清结果，不清意图。 */
export const resolveCartOfferAfterPricingFailure = (
  state: AutoOfferStore,
  explicitlyInvalid = false,
): AutoOfferStore => ({
  ...clearFailedCouponSelection(state, explicitlyInvalid),
  applied_offer: normalizeAppliedOffer(undefined),
});

/**
 * 计价失败时，接口没有可靠的新金额可覆盖，因此保留上一轮金额；
 * 但优惠和赠品资格都属于本次计价结果，必须清空，避免继续展示旧资格。
 */
export const clearFailedCartPricingPresentation = (totalData: ITotalData): ITotalData => ({
  ...totalData,
  code_discount: '0.00',
  point_discount: '0.00',
  applied_offer: undefined,
  show_gift: undefined,
  cart_display_modules: undefined,
});

export const restoreAutoOfferState = clearAutoOfferCouponSelection;

/**
 * couponMode=auto 表示遵循当前分组的默认策略；实际自动选券仍受分组策略和登录资格约束。
 * 四组默认都不使用积分，用户主动选择积分后与券/码选择独立维护。
 */
export const createDefaultAutoOfferStore = (
  state?: Partial<Pick<AutoOfferStore, 'enabled' | 'couponPolicy' | 'authenticated'>>,
): AutoOfferStore => ({
  enabled: state?.enabled ?? IS_US,
  couponPolicy: state?.couponPolicy ?? 'manual',
  authenticated: state?.authenticated ?? false,
  couponMode: 'auto',
  pointMode: 'none',
  code: '',
  expendPoint: undefined,
  applied_offer: normalizeAppliedOffer(undefined),
  coupon: undefined,
});

export const hasExplicitAutoOfferSelection = (state?: AutoOfferStore | null) =>
  Boolean(state && (state.couponMode !== 'auto' || state.pointMode !== 'none'));

/** 结算区的最大优惠提示只服务于用户手动选择的券或优惠码。 */
export const hasManualCouponOrCodeSelection = (state?: AutoOfferStore | null) => {
  if (state?.couponMode === 'manual_coupon') {
    return Boolean(state.coupon);
  }

  return state?.couponMode === 'manual_code' && Boolean(state.code?.trim());
};

export const normalizeAppliedOffer = (raw: any): NormalizedAppliedOffer => ({
  enabled: Boolean(raw?.enabled),
  applied: Boolean(raw?.applied),
  autoApplied: Boolean(raw?.auto_applied),
  show_best_offer_text: Boolean(raw?.show_best_offer_text),
  coupon: {
    source: (raw?.coupon?.source || 'none') as AppliedOfferSource,
    is_best: Boolean(raw?.coupon?.is_best),
    code: raw?.coupon?.code || '',
    detail: raw?.coupon?.detail || {},
    type: raw?.coupon?.type || '',
  },
  point: {
    applied: Boolean(raw?.point?.applied),
    source: (raw?.point?.source || 'none') as AppliedOfferSource,
    availablePoint: Number(raw?.point?.available_point || 0),
    maxExpendPoint: Number(raw?.point?.max_expend_point || 0),
    expendPoint: Number(raw?.point?.expend_point || 0),
    maxDiscount: Number(raw?.point?.max_discount || 0),
  },
  reason: raw?.reason || '',
});

/**
 * 接口文档
 * https://alidocs.dingtalk.com/i/nodes/QG53mjyd80R5bQdxUg3G49M1V6zbX04v
 */
export const buildCartAutoOfferParams = (state?: AutoOfferStore | null): AutoOfferApiParams => {
  if (!state || !IS_US || !state.enabled) return {};

  // 1/3 组走旧优惠协议，任何用户操作都不能重新开启 auto_apply_offer；2/4 组使用新协议。
  const automatic = state.couponPolicy === 'automatic';
  // 游客接口只允许码试算，不能携带券/积分，也不能因自动组而比较或选券。
  if (!state.authenticated) {
    return state.couponMode === 'manual_code' && state.code?.trim()
      ? { ...(automatic ? { auto_apply_offer: 1 as const } : {}), code: state.code.trim() }
      : {};
  }

  // 不传 auto_coupon_compare，避免后端比较最优券时替换用户的手动选择。
  const params: AutoOfferApiParams = automatic ? { auto_apply_offer: 1 } : {};
  switch (state.couponMode) {
    case 'auto':
      break;
    case 'skip':
      if (automatic) params.skip_coupon = 1;
      break;
    case 'manual_coupon':
      if (state.coupon) params.coupon = state.coupon;
      else if (automatic) params.skip_coupon = 1;
      break;
    case 'manual_code':
      if (state.code?.trim()) params.code = state.code.trim();
      else if (automatic) params.skip_coupon = 1;
      break;
  }

  // skip_coupon 只关闭券/码，不能关闭积分；省略 point 会触发后端默认用积分，因此必须显式传 0。
  if (state.pointMode === 'auto') {
    params.point = 1;
  } else if (
    state.pointMode === 'manual' &&
    Number.isInteger(state.expendPoint) &&
    state.expendPoint > 0
  ) {
    params.point = 1;
    params.expend_point = state.expendPoint;
  } else {
    params.point = 0;
  }
  return params;
};

/** 入场不再把用户的指定券/码/取消重置成自动。 */
export const buildCartEnterAutoOfferParams = (state?: AutoOfferStore | null): AutoOfferApiParams =>
  buildCartAutoOfferParams(state);

/** 只回写展示结果；自动命中的券不能变成手动指定券，否则后续改商品会锁住旧券。 */
export const mergeAppliedOfferIntoState = (
  state: AutoOfferStore,
  rawAppliedOffer: any,
  legacyPricing?: Pick<ITotalData, 'code_discount' | 'point_discount'>,
): AutoOfferStore => {
  const applied_offer = normalizeAppliedOffer(rawAppliedOffer);

  // 手动组旧响应不含 applied_offer。仅依据成功报价的实际抵扣和明确意图回显，不能当作自动命中。
  if (state.couponPolicy === 'manual' && rawAppliedOffer == null && legacyPricing) {
    const couponApplied =
      hasManualCouponOrCodeSelection(state) &&
      Math.abs(normalizeAmount(legacyPricing.code_discount)) > 0;
    const pointApplied =
      state.pointMode !== 'none' && Math.abs(normalizeAmount(legacyPricing.point_discount)) > 0;
    applied_offer.legacy = true;
    applied_offer.applied = couponApplied;
    applied_offer.coupon.source = couponApplied ? 'manual' : 'none';
    applied_offer.coupon.type = state.couponMode === 'manual_code' ? 'code' : 'coupon';
    applied_offer.coupon.code = couponApplied ? state.code?.trim() || '' : '';
    applied_offer.point.applied = pointApplied;
    applied_offer.point.source = pointApplied ? 'manual' : 'none';
    // 旧响应只提供积分抵扣金额，不推算最大/已用积分数；可操作性由摘要独立处理。
  }

  return {
    ...state,
    applied_offer,
  };
};
